import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";

type WebsiteContent = {
  hero_title: string;
  hero_subtitle: string;
  about_title: string;
  about_text: string;
  services_title: string;
  gallery_title: string;
  contact_title: string;
  is_published: boolean;
};

const DEFAULTS: WebsiteContent = {
  hero_title: "Capturing Moments That Last Forever",
  hero_subtitle: "Professional wedding photography and cinematic films crafted with passion, artistry, and attention to every detail.",
  about_title: "Storytelling Through the Lens",
  about_text: "We believe every love story deserves to be told with authenticity and artistry. Our approach blends documentary candor with editorial elegance, creating images that feel both timeless and deeply personal.",
  services_title: "Our Services",
  gallery_title: "Recent Work",
  contact_title: "Let\u2019s Create Something Beautiful",
  is_published: true,
};

const SECTIONS = [
  {
    key: "hero", label: "Hero Section", icon: "ri-image-2-line", color: "bg-violet-500/10 text-violet-500",
    fields: [
      { key: "hero_title", label: "Hero Title", type: "text", maxLen: 100, hint: "Main headline on the hero image" },
      { key: "hero_subtitle", label: "Hero Subtitle", type: "textarea", maxLen: 300, hint: "Supporting text below the title" },
    ],
  },
  {
    key: "about", label: "About Section", icon: "ri-user-heart-line", color: "bg-rose-500/10 text-rose-500",
    fields: [
      { key: "about_title", label: "About Title", type: "text", maxLen: 80, hint: "Heading of the about section" },
      { key: "about_text", label: "About Text", type: "textarea", maxLen: 800, hint: "Story text describing your studio" },
    ],
  },
  {
    key: "sections", label: "Section Headings", icon: "ri-layout-line", color: "bg-amber-500/10 text-amber-600",
    fields: [
      { key: "services_title", label: "Services Section Title", type: "text", maxLen: 80, hint: "" },
      { key: "gallery_title", label: "Gallery Section Title", type: "text", maxLen: 80, hint: "" },
      { key: "contact_title", label: "Contact Section Title", type: "text", maxLen: 80, hint: "" },
    ],
  },
];

interface Props {
  tenantId: string;
  onPublishChange?: (published: boolean) => void;
}

const INPUT = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:ring-2 focus:ring-[#C8A96E]/10 transition-all";

export default function ContentTab({ tenantId, onPublishChange }: Props) {
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState<WebsiteContent>(DEFAULTS);

  const fetchData = useCallback(async () => {
    const { data } = await supabase.from("tenant_website_content").select("*").eq("tenant_id", tenantId).maybeSingle();
    if (data) {
      setExists(true);
      setForm({
        hero_title: data.hero_title ?? DEFAULTS.hero_title,
        hero_subtitle: data.hero_subtitle ?? DEFAULTS.hero_subtitle,
        about_title: data.about_title ?? DEFAULTS.about_title,
        about_text: data.about_text ?? DEFAULTS.about_text,
        services_title: data.services_title ?? DEFAULTS.services_title,
        gallery_title: data.gallery_title ?? DEFAULTS.gallery_title,
        contact_title: data.contact_title ?? DEFAULTS.contact_title,
        is_published: data.is_published ?? true,
      });
    } else {
      setExists(false);
      setForm(DEFAULTS);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (key: string, val: string | boolean) => setForm((p) => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    const payload = { ...form, tenant_id: tenantId, updated_at: new Date().toISOString() };
    let error = null;
    if (exists) {
      const res = await supabase.from("tenant_website_content").update(payload).eq("tenant_id", tenantId);
      error = res.error;
    } else {
      const res = await supabase.from("tenant_website_content").insert(payload);
      error = res.error;
      if (!error) setExists(true);
    }
    setSaving(false);
    if (!error) { setSaved(true); setTimeout(() => setSaved(false), 3000); onPublishChange?.(form.is_published); }
  };

  const F = form as Record<string, string | boolean>;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1,2,3].map(i => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Publish toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between">
        <div>
          <p className="text-slate-800 font-semibold text-sm">Page Visibility</p>
          <p className="text-slate-500 text-xs mt-0.5">
            {form.is_published ? "Your page is visible to the public." : "Your page is hidden — visitors will see a 404."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-medium ${form.is_published ? "text-emerald-600" : "text-slate-400"}`}>
            {form.is_published ? "Published" : "Hidden"}
          </span>
          <button
            type="button"
            onClick={() => set("is_published", !form.is_published)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${form.is_published ? "bg-emerald-500" : "bg-slate-200"}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.is_published ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
      </div>

      {/* Section editors */}
      {SECTIONS.map((section) => (
        <div key={section.key} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className={`flex items-center gap-3 px-5 py-3.5 border-b border-slate-100`}>
            <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${section.color}`}>
              <i className={`${section.icon} text-sm`}></i>
            </div>
            <h3 className="text-slate-700 font-semibold text-sm">{section.label}</h3>
          </div>
          <div className="p-5 space-y-4">
            {section.fields.map((field) => (
              <div key={field.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-slate-600 text-xs font-medium uppercase tracking-wider">{field.label}</label>
                  <span className={`text-[10px] font-mono ${(F[field.key] as string).length > field.maxLen * 0.9 ? "text-amber-500" : "text-slate-300"}`}>
                    {(F[field.key] as string).length}/{field.maxLen}
                  </span>
                </div>
                {field.type === "textarea" ? (
                  <textarea
                    value={F[field.key] as string}
                    onChange={(e) => set(field.key, e.target.value)}
                    rows={field.key === "about_text" ? 5 : 3}
                    maxLength={field.maxLen}
                    placeholder={DEFAULTS[field.key as keyof WebsiteContent] as string}
                    className={INPUT + " resize-none"}
                  />
                ) : (
                  <input
                    type="text"
                    value={F[field.key] as string}
                    onChange={(e) => set(field.key, e.target.value)}
                    maxLength={field.maxLen}
                    placeholder={DEFAULTS[field.key as keyof WebsiteContent] as string}
                    className={INPUT}
                  />
                )}
                {field.hint && <p className="text-slate-400 text-[11px] mt-1">{field.hint}</p>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Save button */}
      <div className="flex items-center justify-between pt-2">
        <p className="text-slate-400 text-xs">
          {exists ? "Content saved in database" : "No content saved yet — using defaults"}
        </p>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving…</>
          ) : saved ? (
            <><i className="ri-check-line"></i>Saved!</>
          ) : (
            <><i className="ri-save-line"></i>Save Changes</>
          )}
        </button>
      </div>
    </div>
  );
}
